import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCheckCircle, faTimesCircle, faEye } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  pending: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: faClock },
  approved: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: faCheckCircle },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: faTimesCircle },
};

const TYPE_LABELS = {
  recipe: 'Recipe',
  activity: 'Activity',
  story: 'Story',
};

export default function MyContributions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected

  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'submissions'),
      where('submittedBy', '==', user.uid),
      orderBy('submittedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const subs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubmissions(subs);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading submissions:', error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [user?.uid]);

  const filteredSubmissions = filter === 'all' 
    ? submissions 
    : submissions.filter(sub => sub.status === filter);

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_COLORS[status] || STATUS_COLORS.pending;
    const Icon = statusConfig.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
        <FontAwesomeIcon icon={Icon} className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleViewContent = (submission) => {
    if (submission.status === 'approved' && submission.contentId) {
      // Navigate to the approved content
      navigate(`/${submission.type}/${submission.contentId}`);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-card shadow-card border border-border p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-text-secondary">Loading your contributions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-card shadow-card border border-border p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text-primary mb-2">My Contributions</h2>
        <p className="text-sm text-text-secondary">
          View and track the status of your submitted content
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {['all', 'pending', 'approved', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors capitalize ${
              filter === status
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {status === 'all' ? 'All' : status}
            {status !== 'all' && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                {submissions.filter(s => s.status === status).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-text-secondary mb-2">
            {filter === 'all' 
              ? "You haven't submitted any content yet."
              : `No ${filter} submissions.`
            }
          </div>
          <button
            onClick={() => navigate('/explore')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold"
          >
            Submit Content
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubmissions.map((submission) => (
            <div
              key={submission.id}
              className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-semibold text-text-secondary uppercase">
                      {TYPE_LABELS[submission.type] || submission.type}
                    </span>
                    {getStatusBadge(submission.status)}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-text-primary mb-1">
                    {submission.content?.title || 'Untitled'}
                  </h3>
                  
                  {submission.content?.description && (
                    <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                      {submission.content.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-text-secondary">
                    <span>
                      Submitted {submission.submittedAt && formatDistanceToNow(submission.submittedAt.toDate(), { addSuffix: true })}
                    </span>
                    {submission.reviewedAt && (
                      <span>
                        Reviewed {formatDistanceToNow(submission.reviewedAt.toDate(), { addSuffix: true })}
                      </span>
                    )}
                  </div>

                  {submission.rejectionReason && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm font-semibold text-red-700 mb-1">Rejection Reason:</p>
                      <p className="text-sm text-red-600">{submission.rejectionReason}</p>
                    </div>
                  )}
                </div>

                {submission.status === 'approved' && submission.contentId && (
                  <button
                    onClick={() => handleViewContent(submission)}
                    className="flex-shrink-0 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                    View
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

